// src/scenarioStore.js
//
// ═══════════════════════════════════════════════════════════════
// SCENARIO + CONTACT HISTORY — the durable copy
// ═══════════════════════════════════════════════════════════════
// Every pricing run, every pitch, every note and every disposition gets a row
// in `scenarios` / `contact_log` (see supabase/2026-08-20-scenarios-and-
// contacts.sql). This is the permanent, queryable copy; the jsonb blobs on the
// loan row stay as a capped cache so the UI still has history offline and so
// nothing breaks before the migration is run.
//
// DEGRADES QUIETLY. If the tables don't exist yet, the first write gets a
// PostgREST 42P01 ("undefined table"), we latch a flag, stop trying, and the
// app carries on using jsonb alone. A pricing run must never fail because a
// migration is pending — losing the call is worse than losing the row.
//
// A FILE THAT ISN'T SAVED YET is not an error. A lead's first pitch or
// disposition logs rows for a pipeline file whose loan row the autosave
// hasn't written yet (loan_id → loans(id) fails with 23503); those rows are
// retried in the background and, failing that, kept with loan_id null and
// linked later (or, if the session ends mid-wait, queued and sent again at
// the next login) — see "A brand-new file's first rows" below.

//
// WHO RAN IT. A row may carry `owner` ("reece" | "max" — whoever priced it;
// Max's rows never carry a lead, and a file only when it is one of his own —
// see insertLimited). Until the
// column exists, the first insert that sends it gets PostgREST's 42703 /
// PGRST204; that insert is retried once WITHOUT it and the table is
// remembered as owner-less for the session, so no row is ever lost to it.
//
// PRACTICE MODE writes nothing. logScenario / logContact take an options
// object; {practice:true} — or a row owned by staff whose Practice switch is
// on (or whose prefs haven't loaded yet: access.isPracticeOrUnknown) — is a
// no-op that resolves false. Nothing is queued for later either.

import { supabase, isSupabaseReady } from './supabase';
import { isPracticeOrUnknown, isLimitedStaff } from './access';

// null = not probed yet, true = tables exist, false = migration pending
let _ready = null;
let _probe = null;
let _warned = false;
let _gen = 0;          // bumped by resetScenarioSession (sign-out)
// Tables found to have no `owner` column this session (see insertRow).
let _noOwnerCol = Object.create(null);

// A column the insert named isn't there (42703 from Postgres, PGRST204 from
// PostgREST's schema cache: "Could not find the 'owner' column of …"). Checked
// BEFORE tableMissing, whose message test would otherwise read it as a
// missing table and switch history off for the session.
function columnMissing(err) {
  if (!err) return false;
  return err.code === "42703" || err.code === "PGRST204"
    || /column .* does not exist|could not find the '[^']*' column/i.test(err.message || "");
}

function tableMissing(err) {
  if (!err) return false;
  if (columnMissing(err)) return false;
  // PostgREST surfaces a missing relation as 42P01, and as PGRST205 when the
  // schema cache hasn't picked it up. Either way: not migrated.
  return err.code === "42P01" || err.code === "PGRST205"
    || /does not exist|schema cache/i.test(err.message || "");
}

// Cheap existence probe, run once per session and shared by every caller.
export function scenariosReady() {
  if (!isSupabaseReady()) return Promise.resolve(false);
  if (_ready !== null) return Promise.resolve(_ready);
  if (_probe) return _probe;
  const gen = _gen;
  _probe = supabase.from('scenarios').select('id').limit(1)
    .then(function (r) {
      const ok = !r.error;                             // a missing table, permissions or network — unavailable
      if (gen !== _gen) return ok;                     // a sign-out reset the session meanwhile: latch nothing
      _ready = ok;
      // Rows from an earlier session still waiting for their file (or for a
      // re-send) get a pass.
      if (ok && !_swept) { _swept = true; if (hasWaiting()) setTimeout(resolvePendingAttaches, 0); }
      return _ready;
    })
    .catch(function () { if (gen === _gen) _ready = false; return false; });
  return _probe;
}

// Synchronous read of the latched flag, for rendering the "migration pending"
// notice without turning every render into a promise.
export function scenariosReadySync() { return _ready; }

function warnOnce(what, err) {
  if (_warned) return;
  _warned = true;
  console.warn(
    "LoanFlow: " + what + " could not be written to the database. " +
    "If you have not run supabase/2026-08-20-scenarios-and-contacts.sql yet, that is why — " +
    "history is still being kept in the loan record meanwhile.",
    err && (err.message || err)
  );
}

// ── Rate environment ──────────────────────────────────────────
// The 10-year swap drives the expected rate and therefore the PLF; the 1-year
// CMT drives the note rate. Capturing both is what makes "who is worth
// re-pricing now that rates moved" answerable later. FOA's sheet is the
// reference because every other sheet falls back to its index in Calc.
export function rateEnvironment(rateSheets) {
  var rs = (rateSheets && rateSheets.foaHecmAdj) || {};
  var cmt = parseFloat(rs.index);
  var swap = parseFloat(rs.swapRate);
  return {
    cmt_index: isNaN(cmt) ? null : cmt,
    swap_rate: isNaN(swap) ? null : swap,
    rate_sheet_date: rs.updated || null,
  };
}

function num(v) { var n = parseFloat(v); return isNaN(n) ? null : n; }

// The scenarios table has no column for a borrower credit, so it rides on
// `terms` — a pitched number has to be reproducible (net_pl and
// cash_to_borrower already include the credit; comp is net of it). A fixed-
// proprietary origination override is already spelled out in the terms Calc
// wrote ("… credited to borrower from your pay"); the Credit to Borrower
// share is added here.
// A pitch that overrode a lender rule (the benefit test, a program that
// doesn't fit — see calcRules.pitchOverrides) says so in `terms` too, unless
// Calc already wrote it there.
function termsWithCredit(p) {
  var t = p.terms || '';
  var extra = Math.round((Number(p.credit) || 0) - (Number(p.origCredit) || 0));
  if (extra > 0) t += (t ? ' | ' : '') + 'Credit to Borrower $' + extra.toLocaleString() + ' from your pay';
  var ov = Array.isArray(p.overrides) ? p.overrides.filter(Boolean) : [];
  if (ov.length && !/\bOverrides?:/.test(t)) t += (t ? ' | ' : '') + 'Overrides: ' + ov.join('; ');
  return t || null;
}

// Build a scenarios row from a product + the inputs it was priced on.
// `owner` (optional) = who ran it; left off the row when not given.
export function scenarioRow(opts) {
  var p = opts.product || {};
  var i = opts.inputs || {};
  return Object.assign(opts.owner ? { owner: String(opts.owner) } : {}, {
    loan_id: opts.loanId || null,
    lead_id: opts.leadId || null,
    kind: opts.kind || 'quote',
    run_at: opts.at || new Date().toISOString(),
    borrower_name: opts.name || '',
    phone: opts.phone || '',
    property: opts.property || '',
    borrower_age: i.age == null ? null : Math.round(i.age),
    dob: i.dob || null,
    home_value: num(i.homeValue),
    mortgage_balance: num(i.mortgageBalance),
    current_pl: num(i.currentPL),
    current_avail: num(i.currentAvail),
    est_closing_costs: num(i.closingCosts),
    product: p.name || null,
    lender: p.lender || null,
    margin: num(p.mgn),
    terms: termsWithCredit(p),
    plf: num(p.plf),
    gross_pl: num(p.gross),
    origination: num(p.orig),
    closing_costs: num(p.cc),
    mip_upfront: num(p.mipUp),
    total_costs: num(p.tc),
    net_pl: num(p.net),
    payoff: num(p.payoff),
    cash_to_borrower: num(p.avail),
    comp: num(p.comp),
    passes_benefit: !!p.passes,
    product_count: opts.productCount == null ? null : opts.productCount,
    all_products: opts.allProducts || [],
    note: opts.note || '',
  }, opts.rateEnv || {});
}

// ── A brand-new file's first rows ─────────────────────────────
// scenarios.loan_id / contact_log.loan_id reference loans(id). A lead's FIRST
// pitch or disposition mints its pipeline file and logs the pitch / the call
// against it in the same moment — before App's autosave has written that
// loan row — so the insert fails with a foreign-key violation (23503), and
// that first-quote history used to be lost. Now, in the background (callers
// never wait on this):
//   0. every row gets its id HERE, before the first try, so every attempt is
//      the same row: an attempt that landed but whose reply was lost (the
//      30s REST timeout, a dropped connection) makes the next one fail 23505
//      (duplicate key), which is read as "already written" — never a copy;
//   1. retry with backoff (FK_RETRY_MS: 2s, 6s, 15s — the autosave normally
//      lands within a few seconds);
//   2. still no file row → remember a PENDING ATTACH {table, id, loanId} (in
//      localStorage, so a reload doesn't lose it) FIRST, then write the row
//      with loan_id null (lead_id kept, so the lead's history still finds
//      it). Link before write: a write whose reply is lost is still linked
//      later, and linking a row that never landed is a harmless no-op;
//   3. the attach is made by the next pass of resolvePendingAttaches(): on a
//      timer (PENDING_RETRY_MS), on attachToLoan(), and when the tables are
//      first found in a session (every login — resetScenarioSession). A
//      by-id link counts only when the update hands the row back, or a read
//      shows it already linked: under RLS (signed out, an expired token) an
//      update matches nothing WITHOUT an error, and that used to read as done;
//   4. once the first FK error is in, a retry that fails some other way (the
//      session ended mid-wait → RLS 42501, a timeout, a network drop), or no
//      session at all, puts the row on a RE-SEND QUEUE (localStorage) rather
//      than dropping it; the same passes send it again, by its own id.
// No pass runs signed out (the page doesn't reload on logout, so a timer can
// outlive the session); everything waits for the next login.
// Never drops the row. An error on the FIRST try that isn't an FK violation
// behaves exactly as before (one console warning, false); a missing table
// still latches the "not migrated" flag.
let FK_RETRY_MS = [2000, 6000, 15000];
let PENDING_RETRY_MS = [30000, 120000, 600000];
const PENDING_KEY = "lf_scenario_attach_v1";
const QUEUE_KEY = "lf_scenario_resend_v1";
const PENDING_MAX = 300;
const PENDING_MAX_AGE_MS = 14 * 864e5;   // past this a link / re-send is dropped (the loan's jsonb copy still has it)
let _pendingTimer = null;
let _pendingRound = 0;
let _resolving = null;
let _swept = false;

function fkViolation(err) {
  return !!err && (err.code === "23503" || /foreign key constraint/i.test(err.message || ""));
}
// Written — now, or by an earlier attempt of the same row (same id → 23505).
function landed(err) {
  return !err || err.code === "23505" || /duplicate key value/i.test(err.message || "");
}
const sleep = function (ms) { return new Promise(function (r) { setTimeout(r, ms); }); };
// A v4 uuid for the row (scenarios.id / contact_log.id are uuid primary keys).
function newRowId() {
  try { if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID(); } catch (e) { /* fall through */ }
  const b = new Uint8Array(16);
  try { crypto.getRandomValues(b); } catch (e) { for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256); }
  b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, function (x) { return (x + 0x100).toString(16).slice(1); }).join("");
  return h.slice(0, 8) + "-" + h.slice(8, 12) + "-" + h.slice(12, 16) + "-" + h.slice(16, 20) + "-" + h.slice(20);
}
// Is someone signed in? As the anon key nothing here can write (RLS), and an
// update says so only by matching no rows. No auth client to ask → assume yes.
async function signedIn() {
  try {
    const a = supabase && supabase.auth;
    if (!a || typeof a.getSession !== "function") return true;
    const r = await a.getSession();
    return !!(r && r.data && r.data.session);
  } catch (e) { return false; }
}
const okTable = function (t) { return t === "scenarios" || t === "contact_log"; };
function readList(key, keep) {
  try {
    const o = JSON.parse(localStorage.getItem(key) || "[]");
    return Array.isArray(o) ? o.filter(keep) : [];
  } catch (e) { return []; }
}
function writeList(key, list) {
  try {
    if (!list.length) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(list.slice(-PENDING_MAX)));
  } catch (e) { /* storage blocked — the timer pass this session still has it */ }
}
function pendingKeyOf(p) { return p.table + "|" + (p.id ? "id:" + p.id : "lead:" + p.leadId) + "|" + p.loanId; }
function readPending() { return readList(PENDING_KEY, function (p) { return p && okTable(p.table) && p.loanId && (p.id || p.leadId); }); }
function writePending(list) { writeList(PENDING_KEY, list); }
function queueKeyOf(q) { return q.table + "|" + q.row.id; }
function readQueue() { return readList(QUEUE_KEY, function (q) { return q && okTable(q.table) && q.row && typeof q.row === "object" && q.row.id; }); }
function writeQueue(list) { writeList(QUEUE_KEY, list); }
function hasWaiting() { return readPending().length > 0 || readQueue().length > 0; }
// fresh: a new entry restarts the timer rounds (a pass re-adding one doesn't).
function addPending(p, fresh) {
  const list = readPending();
  const k = pendingKeyOf(p);
  if (!list.some(function (x) { return pendingKeyOf(x) === k; })) list.push(p);
  writePending(list);
  schedulePending(fresh !== false);
}
// A row to send again later (by its own id — a copy that did land makes the
// re-send a 23505 no-op).
function requeue(table, row, what) {
  const list = readQueue();
  if (!list.some(function (q) { return q.table === table && q.row.id === row.id; })) list.push({ table: table, row: row, what: what || "", at: Date.now() });
  writeQueue(list);
  schedulePending(true);
}
function schedulePending(fresh) {
  if (fresh) _pendingRound = 0;
  if (_pendingTimer || _pendingRound >= PENDING_RETRY_MS.length) return;
  const ms = PENDING_RETRY_MS[_pendingRound++];
  _pendingTimer = setTimeout(function () { _pendingTimer = null; resolvePendingAttaches(); }, ms);
}

// One write; never throws.
async function tryWrite(fn) {
  try { const r = await fn(); return r && r.error ? r.error : null; } catch (e) { return e || new Error("write failed"); }
}
function giveUp(what, err) {
  if (tableMissing(err)) _ready = false;
  warnOnce(what, err);
  return false;
}
const insertOf = function (table, r) { return function () { return supabase.from(table).insert(r); }; };
const hasOwner = function (r) { return !!r && Object.prototype.hasOwnProperty.call(r, 'owner'); };
const withoutOwner = function (r) { const o = Object.assign({}, r); delete o.owner; return o; };
// One insert. A row carrying `owner` into a table that has no such column yet
// (42703 / PGRST204 naming it) is sent once more without it, and the table is
// remembered as owner-less so later rows skip the failed try. Same id both
// times, so it can never land twice. Never throws.
async function insertOnce(table, r) {
  const row = hasOwner(r) && _noOwnerCol[table] ? withoutOwner(r) : r;
  const err = await tryWrite(insertOf(table, row));
  if (err && hasOwner(row) && columnMissing(err) && (!err.message || /owner/i.test(err.message))) {
    _noOwnerCol[table] = true;
    return tryWrite(insertOf(table, withoutOwner(row)));
  }
  return err;
}

// Practice mode: nothing is written, now or later.
function practiceSkip(row, opts) {
  if (opts && opts.practice) return true;
  return !!(row && row.owner && isPracticeOrUnknown(row.owner));
}

// Insert one history row, riding out a file whose loan row isn't saved yet.
// Resolves true once the row is written or safely kept for a later write.
async function insertRow(table, row, what) {
  if (!isSupabaseReady()) return false;
  if (!(await scenariosReady())) return false;
  const r = Object.assign({}, row, { id: (row && row.id) || newRowId() });
  const err = await insertOnce(table, r);
  if (landed(err)) { if (r.loan_id && hasWaiting()) resolvePendingAttaches(); return true; }
  if (!fkViolation(err) || !r.loan_id) return giveUp(what, err);
  return keepUntilFiled(table, r, what);
}
// The file's loan row isn't saved yet. From here the row is never dropped:
// written, written unlinked + a pending link, or queued for a re-send.
async function keepUntilFiled(table, r, what) {
  for (let i = 0; i < FK_RETRY_MS.length; i++) {
    await sleep(FK_RETRY_MS[i]);
    if (_ready === false) return false;          // the tables went away meanwhile
    if (!(await signedIn())) { requeue(table, r, what); return true; }
    const err = await insertOnce(table, r);
    if (landed(err)) return true;
    if (tableMissing(err)) return giveUp(what, err);
    if (!fkViolation(err)) { requeue(table, r, what); return true; }
  }
  return writeUnlinked(table, r, what);
}
// Still no file row: record the link FIRST, then keep the history now.
async function writeUnlinked(table, r, what, fresh) {
  addPending({ table: table, id: r.id, loanId: r.loan_id, leadId: r.lead_id || null, at: Date.now() }, fresh);
  if (!(await signedIn())) { requeue(table, r, what); return true; }
  const err = await insertOnce(table, Object.assign({}, r, { loan_id: null }));
  if (landed(err)) return true;
  if (tableMissing(err)) return giveUp(what, err);
  requeue(table, r, what);
  return true;
}

// A LIMITED LOGIN (Max — access.js) only ever adds his own rows: calculator
// quotes, and since 10/2 pitches / hand-entered deals on HIS OWN files. RLS
// gives him INSERT on scenarios and nothing else (supabase/2026-10-02-max-
// own-pipeline.sql: lead_id null, loan_id null or a file he owns), so: no
// existence probe (a read tells him nothing), no sweep of rows another login
// left queued in this browser, never a lead id, and never contact_log.
// A row naming one of his files keeps that loan_id. If the insert is refused
// for it — the file's row isn't saved yet (23503, retried with the usual
// backoff first), or the policy says no (the 10/2 SQL not run yet, or not his
// file) — the row is written once more with loan_id null, so the history is
// kept either way. (No pending link: he can't UPDATE scenarios.) Never throws.
async function insertLimited(row, what) {
  if (!isSupabaseReady()) return false;
  const r = Object.assign({}, row, { id: (row && row.id) || newRowId(), loan_id: (row && row.loan_id) || null, lead_id: null });
  let err = await insertOnce('scenarios', r);
  if (landed(err)) return true;
  if (r.loan_id) {
    for (let i = 0; i < FK_RETRY_MS.length && fkViolation(err); i++) {
      await sleep(FK_RETRY_MS[i]);
      if (!(await signedIn())) return false;
      err = await insertOnce('scenarios', r);
      if (landed(err)) return true;
    }
    err = await insertOnce('scenarios', Object.assign({}, r, { loan_id: null }));
    if (landed(err)) return true;
  }
  warnOnce(what, err);
  return false;
}
const limitedRow = function (row) { return !!(row && row.owner && isLimitedStaff(row.owner)); };

// Fire-and-forget insert. Returns a promise that always resolves — callers are
// mid-call and must never be blocked or thrown at. `opts.practice` (or a row
// owned by staff in Practice mode) → nothing is written: resolves false.
export function logScenario(row, opts) {
  if (practiceSkip(row, opts)) return Promise.resolve(false);
  if (limitedRow(row)) return insertLimited(row, "a priced scenario");
  return insertRow('scenarios', row, "a priced scenario");
}
export function logContact(row, opts) {
  if (practiceSkip(row, opts)) return Promise.resolve(false);
  if (limitedRow(row)) return Promise.resolve(false);   // contact_log is Reece-only
  return insertRow('contact_log', row, "a call note");
}

// Send the re-send queue again (the caller has checked there is a session).
// A row whose file still isn't saved goes the unlinked + pending-link way.
async function drainQueue() {
  const list = readQueue();
  if (!list.length) return;
  const now = Date.now();
  const done = new Set();
  const failed = new Set();
  for (let i = 0; i < list.length; i++) {
    const q = list[i];
    const k = queueKeyOf(q);
    if (now - (Number(q.at) || 0) > PENDING_MAX_AGE_MS) { done.add(k); continue; }
    let err = await insertOnce(q.table, q.row);
    if (!landed(err) && fkViolation(err) && q.row.loan_id) {
      addPending({ table: q.table, id: q.row.id, loanId: q.row.loan_id, leadId: q.row.lead_id || null, at: Number(q.at) || now }, false);
      err = await insertOnce(q.table, Object.assign({}, q.row, { loan_id: null }));
    }
    if (landed(err)) { done.add(k); continue; }
    if (tableMissing(err)) { _ready = false; break; }
    warnOnce(q.what || "a call's history", err);
    failed.add(k);
  }
  // Re-read: rows queued while this ran are kept.
  writeQueue(readQueue().filter(function (q) { return !done.has(queueKeyOf(q)); })
    .map(function (q) { return failed.has(queueKeyOf(q)) ? Object.assign({}, q, { tries: (Number(q.tries) || 0) + 1 }) : q; }));
}

// One pending link → {err} | {done, linked}. By id: done only when the update
// hands the row back, or a read shows it already linked (an attempt that
// landed WITH its loan_id) — "no rows, no error" is not done, it's all an
// update says under RLS. By lead (attachToLoan's): every unlinked row of
// that lead, so no rows means nothing left — unless the session ended
// meanwhile.
async function linkPending(p) {
  try {
    const upd = supabase.from(p.table).update({ loan_id: p.loanId });
    const r = await (p.id ? upd.eq('id', p.id) : upd.eq('lead_id', p.leadId)).is('loan_id', null).select('id');
    if (r && r.error) return { err: r.error };
    if (r && Array.isArray(r.data) && r.data.length) return { done: true, linked: true };
    if (!p.id) return { done: await signedIn(), linked: false };
    const c = await supabase.from(p.table).select('id,loan_id').eq('id', p.id).limit(1);
    if (c && c.error) return { err: c.error };
    const row = c && Array.isArray(c.data) ? c.data[0] : null;
    return { done: !!(row && row.loan_id), linked: false };
  } catch (e) { return { err: e || new Error("link failed") }; }
}

// Re-send what's queued, then link every pending row to its file. Resolves
// with how many rows were linked; anything whose file still isn't saved stays
// (and gets another pass). Signed out → nothing is tried, nothing is dropped.
export function resolvePendingAttaches() {
  if (_resolving) return _resolving;
  _resolving = (async function () {
    if (!isSupabaseReady()) return 0;
    if (!(await scenariosReady())) return 0;
    if (!hasWaiting()) return 0;
    if (!(await signedIn())) { schedulePending(false); return 0; }
    await drainQueue();
    if (_ready === false) return 0;
    const list = readPending();
    const now = Date.now();
    const settled = new Set();
    const failed = new Set();
    let linked = 0;
    for (let i = 0; i < list.length; i++) {
      const p = list[i];
      const k = pendingKeyOf(p);
      if (now - (Number(p.at) || 0) > PENDING_MAX_AGE_MS) { settled.add(k); continue; }
      const r = await linkPending(p);
      if (r.err) {
        if (tableMissing(r.err)) { _ready = false; return linked; }
        if (!fkViolation(r.err)) warnOnce("the link from a call's history to its new file", r.err);
        failed.add(k);
        continue;
      }
      if (r.done) { settled.add(k); if (r.linked) linked++; } else failed.add(k);
    }
    // Re-read: rows queued while this pass ran are kept.
    const left = readPending().filter(function (p) { return !settled.has(pendingKeyOf(p)); })
      .map(function (p) { return failed.has(pendingKeyOf(p)) ? Object.assign({}, p, { tries: (Number(p.tries) || 0) + 1 }) : p; });
    writePending(left);
    if (left.length || readQueue().length) schedulePending(false);
    return linked;
  })().finally(function () { _resolving = null; });
  return _resolving;
}

// A lead that gets pitched or dispositioned mid-call becomes a pipeline file.
// Rows written earlier in that same call carry only lead_id, so point them at
// the new file too — otherwise the file's own history would start empty and
// the numbers that justified the decision would look like they belonged to
// something else. The file's loan row may not be saved yet (same 23503 as
// above): retried with the same backoff, then left as a pending attach — as
// it is when the session ends during the wait (signed out, an update matches
// nothing and says nothing).
export async function attachToLoan(leadId, loanId) {
  if (!leadId || !loanId) return false;
  if (!isSupabaseReady()) return false;
  if (!(await scenariosReady())) return false;
  const what = "the link from a lead's history to its new file";
  const later = function (tables) { tables.forEach(function (t) { addPending({ table: t, leadId: leadId, loanId: loanId, at: Date.now() }); }); return false; };
  if (!(await signedIn())) return later(['scenarios', 'contact_log']);
  const link = function (t) { return tryWrite(function () { return supabase.from(t).update({ loan_id: loanId }).eq('lead_id', leadId).is('loan_id', null); }); };
  // Each pass links the tables that still failed; [{table, err}] of those left.
  const pass = async function (list) {
    const errs = await Promise.all(list.map(link));
    return list.map(function (t, i) { return { table: t, err: errs[i] }; }).filter(function (x) { return !!x.err; });
  };
  let bad = await pass(['scenarios', 'contact_log']);
  for (let attempt = 0; bad.length; attempt++) {
    const hard = bad.find(function (x) { return !fkViolation(x.err); });
    if (hard) { giveUp(what, hard.err); return false; }
    const tables = bad.map(function (x) { return x.table; });
    if (attempt >= FK_RETRY_MS.length) return later(tables);
    await sleep(FK_RETRY_MS[attempt]);
    if (_ready === false) return false;
    if (!(await signedIn())) return later(tables);
    bad = await pass(tables);
  }
  // The file exists now — anything still waiting on one can be linked too.
  if (hasWaiting()) resolvePendingAttaches();
  return true;
}

// Sign-out (App's resetSessionState). The page doesn't reload, so the timer
// and the "already swept" flag would carry into the next login — and a pass
// can't link anything signed out. Stop the timer and forget the probe: the
// next login probes again, and that sweeps whatever is still waiting (it
// stays in localStorage meanwhile).
export function resetScenarioSession() {
  _gen++;
  if (_pendingTimer) clearTimeout(_pendingTimer);
  _pendingTimer = null; _pendingRound = 0;
  _ready = null; _probe = null; _swept = false;
  _noOwnerCol = Object.create(null);   // the migration may have run since — try the column again
}
// A staff login (App): probe the tables (a one-row read) and send whatever an
// earlier session left waiting — straight away, not at the next call. (A
// pass that ran signed out in between tried nothing, so this is the one
// that links it.) Resolves with how many rows were linked.
export function resumeScenarioSync() {
  return scenariosReady().then(function (ok) { return ok && hasWaiting() ? resolvePendingAttaches() : 0; });
}

// Tests only: fresh module state, short delays, a look at the waiting lists.
export const _testing = {
  reset: function () {
    resetScenarioSession();
    _warned = false; _resolving = null;
    FK_RETRY_MS = [2000, 6000, 15000]; PENDING_RETRY_MS = [30000, 120000, 600000];
    try { localStorage.removeItem(PENDING_KEY); localStorage.removeItem(QUEUE_KEY); } catch (e) { /* ignore */ }
  },
  setDelays: function (fk, pending) { if (fk) FK_RETRY_MS = fk.slice(); if (pending) PENDING_RETRY_MS = pending.slice(); },
  pending: function () { return readPending(); },
  queue: function () { return readQueue(); },
  timerArmed: function () { return !!_pendingTimer; },
  noOwnerColumn: function (t) { return !!_noOwnerCol[t || 'scenarios']; },
  PENDING_KEY: PENDING_KEY,
  QUEUE_KEY: QUEUE_KEY,
};

// ── Reads ─────────────────────────────────────────────────────

// Full uncapped history for one record. Pass whichever ids you have; a lead
// that was later promoted has rows under both, so we OR them together.
export async function fetchScenarios(loanId, leadId, limit) {
  if (!isSupabaseReady()) return [];
  if (!(await scenariosReady())) return [];
  const ors = [];
  if (loanId) ors.push('loan_id.eq.' + loanId);
  if (leadId) ors.push('lead_id.eq.' + leadId);
  if (!ors.length) return [];
  try {
    const r = await supabase.from('scenarios').select('*')
      .or(ors.join(',')).order('run_at', { ascending: false }).limit(limit || 100);
    return (r.error || !r.data) ? [] : r.data;
  } catch (e) { return []; }
}

export async function fetchContacts(loanId, leadId, limit) {
  if (!isSupabaseReady()) return [];
  if (!(await scenariosReady())) return [];
  const ors = [];
  if (loanId) ors.push('loan_id.eq.' + loanId);
  if (leadId) ors.push('lead_id.eq.' + leadId);
  if (!ors.length) return [];
  try {
    const r = await supabase.from('contact_log').select('*')
      .or(ors.join(',')).order('at', { ascending: false }).limit(limit || 200);
    return (r.error || !r.data) ? [] : r.data;
  } catch (e) { return []; }
}

// The call-back list's "Done" marks (kind "callback-done", src/callbacks.js)
// since `sinceIso` — one read, every device's marks (it is a table, so they
// are per account, not per device). → {ok, rows} — ok:false when there is no
// table / database to ask (the list then keeps this device's marks only).
// `unavailable: true` only when the table can't take a mark at all (no
// database, the tables aren't there) — a read that merely failed (network,
// a passing PostgREST error) says nothing about where marks are saved
// (Callbacks review CB-7, 10/10).
export async function fetchCallbackDone(sinceIso, limit) {
  if (!isSupabaseReady()) return { ok: false, rows: [], unavailable: true };
  if (!(await scenariosReady())) return { ok: false, rows: [], unavailable: true };
  try {
    const r = await supabase.from('contact_log').select('at,phone,kind,reason')
      .eq('kind', 'callback-done').gte('at', sinceIso).order('at', { ascending: false }).limit(limit || 1000);
    // never latches: a pricing run must not stop logging over this read.
    // PGRST002 ("…schema cache. Retrying.") is PostgREST's brief 503 — a
    // failed read, never a missing table.
    if (r.error) return { ok: false, rows: [], unavailable: r.error.code !== "PGRST002" && tableMissing(r.error) };
    return { ok: true, rows: r.data || [] };
  } catch (e) { return { ok: false, rows: [], unavailable: false }; }
}

// ── The re-price sweep ────────────────────────────────────────
// Everyone priced when the 10-year swap was at least `minDrop` points ABOVE
// where it sits today. A lower expected rate means a higher PLF on the same
// house, so these are the people whose numbers improved without them doing
// anything — the call list for the day rates move.
//
// ONE row per person, and a person qualifies on their NEWEST run only: a
// re-price or re-quote at today's swap means he has already worked them at
// this market, so an old high-swap run must not keep them on the list.
// Calling the same borrower twice off two old quotes is worse than not
// calling at all.

// Who a run belongs to. The same human can appear under a loan id, a lead id
// and a phone number across a long history; any one in common is the same
// person. A run that names both a lead and a loan (a lead that became a file)
// ties the two together, so an older lead-only run can't resurface. `_keys`
// carries identities already absorbed from that person's older runs.
export function personKeys(s) {
  const ph = String((s && s.phone) || '').replace(/[^0-9]/g, '').slice(-10);
  const keys = [s.loan_id && 'loan:' + s.loan_id, s.lead_id && 'lead:' + s.lead_id, ph && 'ph:' + ph].filter(Boolean);
  if (Array.isArray(s._keys)) s._keys.forEach(function (k) { if (keys.indexOf(k) < 0) keys.push(k); });
  if (!keys.length) keys.push('id:' + s.id);
  return keys;
}

// When a run happened, as epoch ms. File history carries `run_epoch` (some
// older snapshots have an epoch but no ISO time); table rows carry run_at.
function runTime(s) {
  const e = Number(s && s.run_epoch);
  if (e > 0) return e;
  const t = Date.parse((s && s.run_at) || '');
  return isNaN(t) ? 0 : t;
}

// A run with nobody behind it: no file, no lead, no phone and no name — the
// standalone calculator's own quotes (Max's runs, owner-tagged, tied to no
// one). There is no one to call back, so the radar never lists it. A run
// whose file or lead was later deleted still carries the name/phone it was
// denormalized with, and stays.
export function anonymousRun(s) {
  if (!s) return true;
  const ph = String(s.phone || '').replace(/[^0-9]/g, '');
  return !s.loan_id && !s.lead_id && !ph && !String(s.borrower_name || '').trim();
}

// Feed runs NEWEST FIRST. Keeps each person's newest run that recorded a swap
// (a run without one never decides anything) and folds the identities of
// their older runs into that row's `_keys`. Anonymous runs are skipped.
function personCollapser() {
  const seen = Object.create(null);
  const people = [];
  return {
    people: people,
    add: function (s) {
      if (!s || isNaN(parseFloat(s.swap_rate))) return;
      if (anonymousRun(s)) return;
      const keys = personKeys(s);
      let owner = null;
      for (let i = 0; i < keys.length && !owner; i++) owner = seen[keys[i]] || null;
      if (owner) {
        keys.forEach(function (k) { if (!seen[k]) { seen[k] = owner; owner._keys.push(k); } });
        return;
      }
      const row = Object.assign({}, s, { _keys: keys.slice() });
      keys.forEach(function (k) { seen[k] = row; });
      people.push(row);
    },
  };
}

function overThreshold(people, currentSwap, minDrop, limit) {
  const cur = parseFloat(currentSwap);
  if (isNaN(cur)) return [];
  const threshold = cur + (minDrop == null ? 0.125 : minDrop);
  const out = [];
  people.forEach(function (p) {
    const sw = parseFloat(p.swap_rate);
    if (sw < threshold - 1e-9) return;
    const row = Object.assign({}, p, { swap_rate: sw, swapDrop: Number((sw - cur).toFixed(3)) });
    delete row._keys;
    out.push(row);
  });
  return out.slice(0, limit || 500);
}

// Every person in the table with their newest swap-stamped run — NO threshold.
// Pages newest-first through the whole table (collapsing as it goes, so memory
// is per person, not per run). { rows, ready:false } when the table is missing.
export async function fetchScenarioPeople() {
  if (!isSupabaseReady()) return { rows: [], ready: false };
  if (!(await scenariosReady())) return { rows: [], ready: false };
  try {
    const PAGE = 1000, MAX_PAGES = 50;
    const c = personCollapser();
    for (let pg = 0; pg < MAX_PAGES; pg++) {
      const r = await supabase.from('scenarios').select('*')
        .order('run_at', { ascending: false })
        .range(pg * PAGE, pg * PAGE + PAGE - 1);
      if (r.error || !r.data) { if (pg === 0) return { rows: [], ready: true, error: true }; break; }
      r.data.forEach(c.add);
      if (r.data.length < PAGE) break;
    }
    return { rows: c.people, ready: true };
  } catch (e) { return { rows: [], ready: true, error: true }; }
}

// The table alone (newest qualifying run per person, newest first).
export async function fetchRepriceCandidates(currentSwap, minDrop, limit) {
  if (!isSupabaseReady()) return { rows: [], ready: false };
  if (!(await scenariosReady())) return { rows: [], ready: false };
  const cur = parseFloat(currentSwap);
  if (isNaN(cur)) return { rows: [], ready: true };
  const r = await fetchScenarioPeople();
  if (!r.ready) return { rows: [], ready: false };
  return { rows: overThreshold(r.rows, cur, minDrop, limit), ready: true };
}

// ── Both sources at once ──────────────────────────────────────
// The table only holds runs logged since it was created, while every file
// still carries its own priced history (follow_ups._wf scenarioHistory /
// quoteLog — see lib.repriceCandidatesFromLoans). Reading the table alone made
// every file priced before the migration vanish from the radar, so the radar
// reads BOTH: `dbRows` (table rows, or fetchScenarioPeople output) and
// `localRows` (repriceCandidatesFromLoans run with no threshold, i.e. each
// file's newest run). They are merged by person — loan id, lead id, phone,
// including a lead that became a file — and each person keeps only their
// NEWEST run from EITHER source; only then is the threshold applied, so
// someone re-priced today drops off whichever side holds that newer run.
// On an exact time tie the table row wins (same run, dual-written; richer row).
//
// Every row comes back in the one shape the radar renders: id, loan_id,
// lead_id, kind, run_at, borrower_name, phone, property, home_value, product,
// lender, cash_to_borrower, swap_rate (number), swapDrop. Rows from files keep
// `_local:true`. Newest first; `limit` defaults to 500 (Infinity = all).
export function mergeRepriceCandidates(dbRows, localRows, currentSwap, minDrop, limit) {
  const cur = parseFloat(currentSwap);
  if (isNaN(cur)) return [];
  const all = [];
  (dbRows || []).forEach(function (s, i) { if (s) all.push({ s: s, t: runTime(s), src: 0, i: i }); });
  (localRows || []).forEach(function (s, i) { if (s) all.push({ s: Object.assign({}, s, { _local: true }), t: runTime(s), src: 1, i: i }); });
  all.sort(function (a, b) { return (b.t - a.t) || (a.src - b.src) || (a.i - b.i); });
  const c = personCollapser();
  all.forEach(function (x) { c.add(x.s); });
  return overThreshold(c.people.map(normalizeRadarRow), cur, minDrop, limit);
}

function normalizeRadarRow(s) {
  const n = function (v) { const x = parseFloat(v); return isNaN(x) ? null : x; };
  return Object.assign({}, s, {
    id: s.id != null ? s.id : (s.loan_id || s.lead_id || ''),
    loan_id: s.loan_id || null,
    lead_id: s.lead_id || null,
    kind: s.kind || 'quote',
    run_at: s.run_at || (Number(s.run_epoch) > 0 ? new Date(Number(s.run_epoch)).toISOString() : null),
    borrower_name: s.borrower_name || '',
    phone: s.phone || '',
    property: s.property || '',
    home_value: n(s.home_value),
    product: s.product || null,
    lender: s.lender || null,
    cash_to_borrower: n(s.cash_to_borrower),
  });
}
